<template>
  <div class="d-block relative" style="height: 36px;">

    <error-dialog :status="throwErrorStatus" />

    <dialog-scroll-lock v-if="confirmMsg" width="300" persistent :value="true" @input="confirmMsg = null">
      <v-card class="pt-4">
        <v-card-title class="pt-0">Are you sure?</v-card-title>
        <v-card-text class="break-word" v-html="confirmMsg" />
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary" @click="skipDuplicateCheck = false; confirmMsg = null;">Cancel</v-btn>
          <v-btn class="relative overflow-hidden" text :loading="attachPayPalButtonsStatus.pending || orderPending">
            Continue
            <div class="absolute-fill my-n1 mx-n2" style="opacity: 0.001;" ref="btn" :id="btnId" />
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <!-- This button is strictly for appearances -->
    <v-btn class="absolute-fill" style="z-index: 1; background: rgb(254,195,73);" loading />

    <!-- We use this button to cover up the actual PayPal button as needed (i.e. disabled / loading) -->
    <v-btn
      v-if="attachPayPalButtonsStatus.pending || orderPending"
      class="absolute-fill"
      style="z-index: 1001; background: none; pointer-events: none;"
      elevation="0"
    />

    <div v-if="!confirmMsg" ref="btn" :id="btnId" />
  </div>
</template>


<script>
import ErrorDialog from '@/components/ErrorDialog.vue';
import { LOCALE, TZ } from '@/utils/intl.js';
import { OPError } from '@/utils/op-dev-fetch';
import uuid4 from '@/utils/uuid.js';


export default {
  name: 'paypal',

  components: {
    ErrorDialog
  },

  props: [ 'itemized', 'metadata' ],

  data() {
    return {
      throwErrorStatus: this.$track('throwError'),
      attachPayPalButtonsStatus: this.$track('attachPayPalButtons'),
      orderPending: false,
      btnId: null,
      confirmMsg: null,
      skipDuplicateCheck: false
    };
  },

  mounted() {
    this.attachPayPalButtons();
  },

  beforeDestroy() {
    this.detachPayPalButtons();
  },

  methods: {
    async throwError(e) {
      // This is a little weird, but it's the easiest way to tie into the error-dialog
      throw e;
    },

    async callWrapError(route, params, progress) {
      // PayPal wraps / transforms error subclasses, making specific messages unparseable from the 'onError' handler
      try {
        return await this.$call(route, params);
      } catch (e) {
        if (e instanceof OPError)
          // This seems pointless, but I'm pretty sure it's necessary in order to pass the error data to PayPal's
          // 'onError' callback
          throw { type: e.type, message: e.message };
        throw e;
      }
    },

    async attachPayPalButtons() {
      // This has the side effect of setting 'paypal' on the window
      await loadPaypalPromise;

      // Make sure we mount to an element with a different ID to avoid issues with the PayPal API
      if (this.btnId) {
        const el = document.getElementById(this.btnId);
        while (el.firstChild)
          el.removeChild(el.firstChild);
      }
      this.btnId = `paypal-btn-${uuid4()}`;
      await this.$nextTick();

      // https://developer.paypal.com/docs/checkout/integrate/#3-render-the-smart-payment-buttons
      // https://developer.paypal.com/demo/checkout/#/pattern/server
      // https://developer.paypal.com/docs/checkout/reference/upgrade-integration/#4-set-up-the-transaction
      const parent = this;
      const config = {
        // This setting makes only the PayPal button show up
        fundingSource: 'paypal',

        style: { shape: 'rect' },

        async createOrder(data, actions) {
          const createAPI = 'paypal/create-payment-intent';
          parent.$emit('create');
          parent.orderPending = true;
          const params = {
            metadata: parent.metadata,
            locale: LOCALE,
            tz: TZ,
            items: parent.itemized.items,
            skip_duplicate_check: parent.skipDuplicateCheck
          };
          let response = await parent.callWrapError(createAPI, params);
          for (const key of [ 'confirm', 'error', 'price_update' ])
            if (response[key])
              // Throwing a string directly instead of an error object distinguishes our errors from PayPal's
              throw key + ':' + response[key];
          return response.paypal_order_id;
        },

        async onApprove(data, actions) {
          const params = { paypal_order_id: data.orderID };
          const response = await parent.callWrapError('paypal/complete-payment-intent', params);
          parent.orderPending = false;
          if (response.hasOwnProperty('declined'))
            return actions.restart();
          parent.$emit('complete', response);
        },

        onCancel(data, actions) {
          parent.orderPending = false;
          parent.confirmMsg = null;
        },

        onError(e) {
          parent.orderPending = false;
          if (typeof e == 'string') {
            // We threw this directly
            const [ code, msg ] = e.split(/:(.*)/);
            if (code == 'confirm')
              parent.confirmMsg = msg;
            else if (code == 'error')
              parent.throwError(new VisibleError(msg));
            else if (code == 'price_update')
              parent.$emit('price-update');
            else
              parent.throwError(new VisibleError('Unknown error'));
          } else {
            if (e.message == 'Detected popup close')
              // This happens if the user closed the PayPal pop-up while it was still loading
              null;
            else if (e.message == 'PAYMENT_ALREADY_DONE')
              // The idempotency check failed
              return parent.$emit('complete', null);
            else if (e.type == 'LockedPaymentIntent')
              parent.throwError(new VisibleError('Sorry, this account has been locked - please contact us: <a href="mailto:support@ellacard.com">support@ellacard.com</a>'));
            else if (e.type == 'MultipleFailedPaymentIntents')
              parent.throwError(new VisibleError('Sorry, this account has been temporarily locked due to too many incomplete payment attempts - please contact us: <a href="mailto:support@ellacard.com">support@ellacard.com</a>'));
            else if (e.type == 'InvalidMetadataException')
              parent.$emit('metadata-error', e);
            else {
              parent.$call('record-failed-payment-intent');
              parent.throwError(e);
            }
          }

          // It seems that this callback only gets called on the first error - re-attaching the buttons should reset
          // everything
          parent.attachPayPalButtons();
        }
      };
      paypal.Buttons(config).render(`#${this.btnId}`);
    },

    detachPayPalButtons() {
      // Make sure that the payment screen disappears to avoid duplicate payments
      // https://stackoverflow.com/a/57974343/8439453
      if (this.$refs.btn)
        this.$refs.btn.innerHTML = '';
    }
  },

  watch: {
    orderPending(v) {
      if (!v)
        this.$emit('cancel');
    },

    async confirmMsg(v) {
      if (v)
        this.skipDuplicateCheck = true;
      await this.$nextTick();
      this.detachPayPalButtons();
      this.attachPayPalButtons();
    }
  }
}
</script>
